import React, { forwardRef } from 'react';
import { styled, Card, CardActionArea } from '@mui/material';

const TYPES = ['milestone', 'project', 'task'];

const CustomCard = styled(Card)(({ type, theme }) => {
    return {
        ...(type === 'milestone' && {
            margin: '-3px',
            borderRadius: '12px',
            width: 'calc(100% + 6px)',
            boxShadow: 'none !important',
            background: 'transparent',
            '&:focus-visible': {
                boxShadow: `inset 0 0 0 3px ${theme.palette.ensightful.secondary[100]} !important`,
                outline: 'none',
            },
            '&:hover': {
                margin: '-12px -12px 11px',
                width: 'calc(100% + 24px)',
                '& > .MuiCardActionArea-root': {
                    margin: '12px',
                    width: 'calc(100% - 24px)',
                    boxShadow: `${theme.palette.ensightful.shadows[1]} !important`,
                },
            },
        }),
        ...(type === 'project' && {
            margin: '-3px',
            width: 'calc(100% + 6px)',
            boxShadow: 'none !important',
            background: 'transparent',
        }),
        ...(type === 'task' && {
            borderRadius: '8px',
            width: '100%',
            boxShadow: `${theme.palette.ensightful.shadows[1]}`,
            '&:focus-visible': {
                outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            },
        }),
    };
});

const CustomCardActionArea = styled(CardActionArea)(({ type, theme }) => {
    return {
        borderRadius: '8px',
        background: theme.palette.ensightful.shades[0],
        '& > .MuiCardActionArea-focusHighlight': {
            display: 'none',
        },
        ...(type === 'milestone' && {
            margin: '3px',
            border: `2px solid ${theme.palette.ensightful.neutral[600]}`,
            width: 'calc(100% - 6px)',
            padding: theme.spacing(4),
        }),
        ...(type === 'project' && {
            margin: '3px',
            border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
            width: 'calc(100% - 6px)',
            padding: theme.spacing(5),
            '&:focus-visible': {
                outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            },
            '&:hover': {
                borderColor: theme.palette.ensightful.primary[500],
                boxShadow: `inset 0 0 0 1px ${theme.palette.ensightful.primary[500]}`,
            },
            '&:active': {
                borderColor: theme.palette.ensightful.primary[700],
                boxShadow: `inset 0 0 0 1px ${theme.palette.ensightful.primary[700]}`,
            },
        }),
        ...(type === 'task' && {
            padding: theme.spacing(4),
        }),
    };
});

export default forwardRef(({ children, type = 'project', onClick, ...rest }, ref) => {
    if (!TYPES.includes(type)) throw new Error(`Shell: Invalid type "${type}" in component Card.`);

    return (
        <CustomCard ref={ref} {...rest} type={type}>
            <CustomCardActionArea
                component={'div'}
                role={'presentation'}
                tabIndex={type === 'project' ? 0 : -1}
                type={type}
                onClick={onClick}
            >
                {children}
            </CustomCardActionArea>
        </CustomCard>
    );
});
